import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { combineLatest, Observable } from "rxjs";
import { StakedAssetFacade } from "@modules/staking/domain/+state/staked-asset.facade";
import { TradableDetailUiModel } from "@modules/staking/domain/entities/tradable-detail-ui.model";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { StakeableAvailability } from "@modules/staking/domain/entities/stakable-availability.model";
import { StakingAmountValidationService } from "@modules/staking/domain/+state/utility/staking-amount-validation.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { StakingModalFlowModel } from "@modules/staking/domain/entities/staking-modal-flow.model";
import { Stakeable } from "@modules/staking/domain/entities/stakeable.model";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { StakingWidgetHandlerModel } from "@modules/staking/domain/entities/staking-widget-handler.model";
import { StakingEntryHelperService } from "@modules/staking/services/staking-entry.helper.service";

@UntilDestroy()
@Component({
	selector: "app-staking-widget-handler",
	templateUrl: "./staking-widget-handler.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class StakingWidgetHandlerComponent implements OnInit, OnDestroy {
	public stakingWidgetUiState$: Observable<StakingWidgetHandlerModel> =
		this.stakedAssetFacade.getStakingWidgetUiState$();
	private readonly stakingEntryHelper = inject(StakingEntryHelperService);

	constructor(
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly stakedAssetFacade: StakedAssetFacade,
		private readonly stakingFacade: StakingFacade,
		private readonly stakingAmountValidationService: StakingAmountValidationService,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService
	) {}

	public ngOnInit(): void {
		combineLatest([this.route.params, this.stakingFacade.getStakeables$(), this.route.queryParams])
			.pipe(untilDestroyed(this))
			.subscribe(([params, stakeables, queryParams]) => {
				this.stakedAssetFacade
					.fetchAndUpdateStakable(params["assetId"])
					.pipe(untilDestroyed(this))
					.subscribe((stakeable) =>
						this.handleStakingDeeplink(params["assetId"], queryParams["stakingModal"], stakeable)
					);
			});
		this.stakingFacade.updateStakingRewards();
	}

	public ngOnDestroy(): void {
		this.stakedAssetFacade.removeStakeable();
	}

	public checkFlowAvailability(rewardsInfo: StakingModalFlowModel): void {
		const { stakeable, modalType } = rewardsInfo;
		const flowClosed: boolean =
			!this.stakingAmountValidationService.hasFundsToStake(stakeable) ||
			stakeable.availability === StakeableAvailability.SOLD_OUT;
		if (!!stakeable && flowClosed && modalType === TRADE_TYPE.STAKE) {
			this.resetStakingProcess(stakeable);
			return;
		}
		this.openModal(stakeable, modalType);
	}

	public showRewardWidget(visibility: TradableDetailUiModel, stakeable: Stakeable): boolean {
		return visibility.isStaked || +stakeable.rewardsAmount.totalCurrent.asset > 0;
	}

	private openModal(stakeable: Stakeable, modalType: string): void {
		if (stakeable.id.length > 0) {
			this.stakingFacade.setWalletByTradableId(stakeable.id);
		}
		this.stakingEntryHelper.openStakingFlow(stakeable.id, modalType);
	}

	public openRewardModal(id: string = ""): void {
		this.router.navigate([{ outlets: { modal: "staking-rewards" } }], {
			queryParams: {
				assetId: id
			},
			state: {
				stakeableId: id
			}
		});
	}

	private resetStakingProcess(stakeable: Stakeable): void {
		// Error case if user doesn't own enough of the asset to stake
		let errorMsg: string;
		const stakeableName: string = stakeable.name;
		if (stakeable.availability === StakeableAvailability.SOLD_OUT) {
			errorMsg = this.translationService.translate("staking_sold_out_banner_text", [stakeableName]);
		} else {
			errorMsg = this.translationService.translate("stake_buy_more_toaster", [stakeableName]);
		}
		this.infoService.setError(errorMsg);
		this.stakingFacade.resetStakingProcess();
	}

	private handleStakingDeeplink(assetId: string, stakingModal: string, stakeable: Stakeable): void {
		if (stakingModal === "true" && assetId) {
			this.checkFlowAvailability({ stakeable, modalType: TRADE_TYPE.STAKE });
		}
	}
}
